import { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
  type Location as RouterLocation,
} from "react-router-dom";

import {
  PreservedReturnNavigationOptions,
  NavigationReturnState,
  defaultOptions,
} from "../../../Model/Navigation.model";

const encodeCurrentLocationForQueryParam = (currentLocation: RouterLocation) =>
  encodeURIComponent(
    `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`
  );

const isAllowedAppInternalPath = (
  candidatePath: string,
  disallowedPrefixes: string[]
) =>
  !!candidatePath &&
  candidatePath.startsWith("/") &&
  !disallowedPrefixes.some((prefix) => candidatePath.startsWith(prefix));

// Reads a "return to" destination from location.state or query-string and Provides helpers to navigate to flows while preserving the current route
//and Provides a computed, sanitized path to navigate back to after the flow completes

export function useNavigationRedirection(
  userOptions?: PreservedReturnNavigationOptions
) {
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const [searchParams] = useSearchParams();

  const { disallowStartsWith, fallback, queryKeys } = {
    ...defaultOptions,
    ...userOptions,
  };

  const preservedReturnPath = useMemo(() => {
    const navigationState =
      (currentLocation.state as NavigationReturnState) || {};
    const structuredReturn = navigationState.returnTo;

    if (
      structuredReturn &&
      (structuredReturn.pathname ||
        structuredReturn.search ||
        structuredReturn.hash)
    ) {
      const candidatePath = `${structuredReturn.pathname ?? "/"}${
        structuredReturn.search ?? ""
      }${structuredReturn.hash ?? ""}`;
      if (isAllowedAppInternalPath(candidatePath, disallowStartsWith)) {
        return candidatePath;
      }
    }

    for (const queryKey of queryKeys) {
      const rawParamValue = searchParams.get(queryKey);
      if (!rawParamValue) continue;

      try {
        const decodedCandidatePath = decodeURIComponent(rawParamValue);
        if (
          isAllowedAppInternalPath(decodedCandidatePath, disallowStartsWith)
        ) {
          return decodedCandidatePath || fallback;
        }
      } catch {}
    }

    return fallback;
  }, [
    currentLocation.state,
    searchParams,
    disallowStartsWith.join("|"),
    queryKeys.join("|"),
    fallback,
  ]);

  //Push to a target route while preserving the current route in navigation state
  //so we can restore it later .

  const navigateWithPreservedReturnState = (
    destinationPath: string,
    originContextLabel?: string,
    replaceHistoryEntry = false
  ) =>
    navigate(destinationPath, {
      state: {
        from: originContextLabel,
        returnTo: {
          pathname: currentLocation.pathname,
          search: currentLocation.search,
          hash: currentLocation.hash,
        },
      } as NavigationReturnState,
      replace: replaceHistoryEntry,
    });

  //Push to a target route while preserving the current route in a query param
  //(example: /auth/login?next=/products/42). Useful for non-stateful links.
  const navigateWithPreservedReturnQuery = (
    destinationBasePath: string,
    queryParamKey = "next",
    replaceHistoryEntry = false
  ) => {
    const separator = destinationBasePath.includes("?") ? "&" : "?";
    navigate(
      `${destinationBasePath}${separator}${queryParamKey}=${encodeCurrentLocationForQueryParam(
        currentLocation
      )}`,
      { replace: replaceHistoryEntry }
    );
  };

  const navigateBackToPreservedRoute = (replaceHistoryEntry = true) =>
    navigate(preservedReturnPath, { replace: replaceHistoryEntry });

  return {
    preservedReturnPath,
    navigateWithPreservedReturnState,
    navigateWithPreservedReturnQuery,
    navigateBackToPreservedRoute,
  };
}
